import { expressApi } from '@/state/api/expressApi';
import type { CurrentUserDto, ProductExperienceDto } from '@/types';

export const userApi = expressApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /me — fetch current authenticated user info.
     * Best-effort: the UI should fall back gracefully if this fails.
     */
    getCurrentUser: builder.query<CurrentUserDto, void>({
      query: () => '/me',
      providesTags: ['User'],
    }),

    /**
     * GET /me/product-experience — the Pendo subscription plus the visitor and
     * account this session reports as. Deliberately NOT tagged `User`: releasing
     * an active workflow invalidates that tag, and analytics has no reason to
     * refetch when the workflow gate changes. Best-effort like /me — a failure
     * means no analytics, never a broken page.
     */
    getProductExperience: builder.query<ProductExperienceDto, void>({
      query: () => '/me/product-experience',
    }),

    /**
     * POST /me/active-workflow/release — abandon the caller's active workflow.
     * Only valid while the workflow is at the LANE_DECISION stage; the backend
     * returns 409 (translation.workflow-not-releasable) otherwise. Idempotent:
     * releasing an already-released workflow returns 204, so a double click is
     * safe. No request body. On success we invalidate `User` so /me re-reports
     * the (now absent) active workflow and the gate opens.
     */
    releaseActiveWorkflow: builder.mutation<void, void>({
      query: () => ({ url: '/me/active-workflow/release', method: 'POST' }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const { useGetCurrentUserQuery, useGetProductExperienceQuery, useReleaseActiveWorkflowMutation } =
  userApi;
